import { Injectable } from '@angular/core';
import { UtmParameters } from '@models/UtmParameters';

@Injectable({
	providedIn: 'root',
})
export class UtmService {
	private readonly key: string = `embrace_analytics`;
	private readonly domain: string = `.embracepetinsurance.com`;
	private readonly defaultValues: UtmParameters = {
		source: `direct`,
		medium: `none`,
		campaign: ``,
		term: ``,
		content: ``,
		referrer: encodeURIComponent(document.referrer),
		gclid: ``,
		userAgent: encodeURIComponent(navigator.userAgent),
	};
	// #region
	private readonly organic: string[] = [
		`about.com`,
		`alice.com`,
		`alltheweb.com`,
		`altavista.com`,
		`aol.com`,
		`ask.com`,
		`baidu.com`,
		`bing.com`,
		`cnn.com`,
		`daum.net`,
		`ekolay.net`,
		`eniro.se`,
		`google.com`,
		`google.co`,
		`kvasir.no`,
		`lycos.com`,
		`mamma.com`,
		`msn.com`,
		`mynet.com`,
		`najdi.org`,
		`naver.com`,
		`search.netscape.com`,
		`online.onetcenter`,
		`ozu.es`,
		`pchome.com`,
		`rambler.ru`,
		`search.com`,
		`sesam.no`,
		`seznam.cz`,
		`szukaczpl`,
		`terra.com`,
		`search.virgilio`,
		`voila.fr`,
		`wp.pl`,
		`yahoo.com`,
		`yam.com`,
		`yandex.com`,
	];
	private readonly social: string[] = [
		`facebook.com`,
		`plus.google`,
		`twitter.com`,
		`instagram.com`,
		`t.co`,
		`hootsuite.com`,
		`linkedin.com`,
		`pinterest.com`,
		`bufferapp.com`,
		`digg.com`,
		`reddit.com`,
		`stumbleupon.com`,
	];
	// #endregion

	private get maxAge(): number {
		const numberOfYears = 2;
		const days = 365;
		const hours = 24;
		const minutes = 60;
		const seconds = 60;

		return numberOfYears * days * hours * minutes * seconds;
	}

	constructor() { }

	public getUtmCookie(): UtmParameters {
		const rawCookie: string = document.cookie
			.split(`;`)
			.map(c => c.trim())
			.find(entry => entry.includes(this.key))
			?.split(`${this.key}=`)[1] ?? ``;

		return rawCookie ? JSON.parse(rawCookie) : { };
	}

	public setUtmValues(values: UtmParameters & { gclid?: string }): void {
		// eslint-disable-next-line
		const _values = JSON.parse(JSON.stringify(values), (key, value) => {
			return !!value.map ? value[0] : value;
		});
		const {
			source = ``,
			medium = ``,
			campaign = ``,
			term = ``,
			content = ``,
			gclid = ``,
			userAgent = encodeURIComponent(navigator.userAgent),
		} = _values;
		// referrer needs to be set so we don't keep the referrer of the previous page
		const referrer = encodeURIComponent(document.referrer);
		let utmValues: UtmParameters = {
			...this.defaultValues,
			referrer,
		};

		if (!!Object.keys(this.getUtmCookie()).length) {
			utmValues = this.getUtmCookie();
		}

		/** if the query params have source and medium use those  */
		if (!!source && !!medium) {
			utmValues = {
				source,
				medium,
				campaign,
				term,
				content,
				gclid,
				referrer,
				userAgent,
			};
		} else {
			/** If none of those params exist, set values based on the referrer */
			if (document.referrer && !document.referrer.includes(`embracepetinsurance.com`) && !(/localhost/i.test(document.referrer))) {
				const parsedReferrer: string = document.referrer
					.replace(/^((http)?s?:?\/{0,2}(www\.)?)/img, ``)
					.split(`/`)?.[0]
					.replace(/[^a-z0-9\.]/g, '');
				utmValues = {
					campaign: gclid,
					term: ``,
					content: ``,
					source: parsedReferrer,
					/** Check social and organic arrays for referrer, if it is there set the medium accordingly otherwise set to referral */
					medium: this.organic.includes(parsedReferrer)
						? `organic`
						: this.social.includes(parsedReferrer)
							? `social`
							: `referral`,
					gclid,
					referrer,
					userAgent,
				};
			}
		}

		/** Set the cookie to reflect the passed UTM params */
		document.cookie = /localhost/.test(location.host)
			? `${this.key}=${JSON.stringify(utmValues)};samesite=lax;max-age=${this.maxAge};path=/`
			: `${this.key}=${JSON.stringify(utmValues)};samesite=lax;max-age=${this.maxAge};domain=${this.domain};path=/`;
	}
}
